import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  SvgIcon,
} from '@mui/material';
import FilterPanel from 'components/shared/filter-panel/FilterPanel';
import CustomTooltip from 'components/shared/tooltip';
import Permissions from 'const/Permissions';
import usePermissions from 'hooks/usePermissions';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as WarningIcon } from 'assets/icons/warning_icon.svg';
import { PlatformType } from 'modules/modals/const/Constants';
import { isWhitelabelPortal } from 'const/PortalType';

const styles = {
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    '& .MuiFormControlLabel-root': {
      width: '100%',
    },
  },
  checkbox: {
    marginRight: '0', // Remove right margin
    padding: '0', // Remove padding
  },
};

const AdvancedSettings = ({ value, onChange, disableNoRollback = false }) => {
  const { t } = useTranslation();

  const handleChange = (event) => {
    const { value } = event.target;
    onChange(value);
  };

  return (
    <FilterPanel
      title={t('views.plan_details.advanced_settings.title')}
      sx={{ p: 1, borderRadius: 1, backgroundColor: '#F5F5F5' }}
    >
      <FormControl>
        <RadioGroup value={value} onChange={handleChange} name="radio-buttons-group">
          <FormControlLabel
            value="install_enable"
            control={<Radio />}
            label={t('views.plan_details.advanced_settings.filters.install_enable')}
          />
          <FormControlLabel
            value="install"
            control={<Radio />}
            label={t('views.plan_details.advanced_settings.filters.install')}
          />
          <FormControlLabel
            disabled={disableNoRollback}
            value="install_no_rollback"
            control={<Radio />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('views.plan_details.advanced_settings.filters.install_no_rollback')}
                <CustomTooltip
                  title={
                    <>
                      {t('views.plan_details.advanced_settings.filters_info')}{' '}
                      {!isWhitelabelPortal(CONFIG.app.portalType) && (
                        <Link
                          target="_blank"
                          component="a"
                          underline="underline"
                          href="https://tealcommunications.atlassian.net/wiki/spaces/TD/pages/1055424513/TEAL+AURORA+User+Guide#Install-%26-Enable-Without-Rollback"
                        >
                          {t('views.plan_details.advanced_settings.filters_info_link')}
                        </Link>
                      )}
                    </>
                  }
                  disableHoverListener={false}
                  arrow={true}
                  placement={'top-end'}
                  PopperProps={{
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [10, 0], // Move tooltip LEFT by 10px
                        },
                      },
                    ],
                  }}
                >
                  <SvgIcon component={WarningIcon} sx={{ width: 16, height: 16 }} inheritViewBox />
                </CustomTooltip>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>
    </FilterPanel>
  );
};

const InstallOptions = ({ value, onChange, disablePreferred = false }) => {
  const { t } = useTranslation();

  const handleToggle = (name) => (event) => {
    onChange({
      ...value,
      [name]: event.target.checked,
    });
  };

  return (
    <Stack sx={styles.checkboxContainer} mt={1}>
      <FormControlLabel
        control={<Checkbox checked={value.fallback} onChange={handleToggle('fallback')} />}
        label={t('views.plan_details.set_fallback')}
      />

      <FormControlLabel
        disabled={disablePreferred}
        control={<Checkbox checked={value.preferred} onChange={handleToggle('preferred')} />}
        label={t('views.plan_details.set_preferred')}
      />
    </Stack>
  );
};

const PlanInstallAction = ({ data, queryFilters = {}, openInstallModal = () => {} }) => {
  const permissions = usePermissions();

  const { t } = useTranslation();

  const [settings, setSettings] = useState('install_enable');
  const [installOptions, setInstallOptions] = useState({
    fallback: false,
    preferred: false,
  });

  const handleInstall = () => {
    const query = {
      ...queryFilters,
      changePlanId: data.planId,
      setAsPrefered: installOptions.preferred,
      lockProfile: installOptions.fallback,
      rollback: ['install_enable', 'install'].includes(settings),
      enable: ['install_enable', 'install_no_rollback'].includes(settings),
    };

    openInstallModal(query, data);
  };

  const isPlatformIot = data?.platformType?.toUpperCase() === PlatformType.IOT;
  const disableNoRollback = data?.platformType === undefined ? false : !isPlatformIot;

  return (
    <>
      <Box style={{ display: 'flex', justifyContent: 'center', marginTop: '25px' }}>
        <Button
          variant="contained"
          disabled={!permissions.hasAll(Permissions.MarketplaceSection.ChangePlanAction)}
          onClick={handleInstall}
          sx={{
            width: '256px',
            height: '46px',
          }}
        >
          {t('views.plan_details.install')}
        </Button>
      </Box>
      <AdvancedSettings value={settings} disableNoRollback={disableNoRollback} onChange={setSettings} />
      <InstallOptions disablePreferred={!isPlatformIot} value={installOptions} onChange={setInstallOptions} />
    </>
  );
};

export default PlanInstallAction;
